import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ---------- Asset upload validation ----------
// HeyGen accepts images, video, and PDF (max 32 MB per asset); our upload lambda
// converts any decodable image (HEIC/AVIF/WebP/GIF/BMP/TIFF…) to JPEG/PNG before
// forwarding, and video is transcoded server-side so larger sources are fine.
// Text documents (txt/md) are COMPOSER-ONLY: read in the browser and handed to the
// Claude planner as context — never uploaded, never sent to HeyGen.
const RX_IMG = /\.(png|jpe?g|gif|webp|bmp|tiff?|heic|heif|avif)$/i;
const RX_VID = /\.(mp4|webm|mov|avi|mkv|3gp|flv|wmv|m4v|ts|mpe?g|3g2)$/i;
const RX_AUD = /\.(mp3|wav|aac|ogg|flac|m4a|wma|opus|aiff)$/i;
const RX_TXT = /\.(txt|md|markdown)$/i;

/** Human-readable supported-type lists for reject messages (injected as {{types}}
 *  so translations can never corrupt the extension names). */
export const SUPPORTED_COMPOSER_TYPES =
  'JPG, PNG, WebP, GIF, HEIC, AVIF, BMP, TIFF · MP4, MOV, WebM, AVI, MKV · MP3, WAV, M4A · PDF · TXT, MD';
export const SUPPORTED_ASSET_TYPES =
  'JPG, PNG, WebP, GIF, HEIC, AVIF, BMP, TIFF · MP4, MOV, WebM, AVI, MKV · PDF';
export const SUPPORTED_ASSET_TYPES_WITH_AUDIO =
  'JPG, PNG, WebP, GIF, HEIC, AVIF, BMP, TIFF · MP4, MOV, WebM, AVI, MKV · MP3, WAV, M4A · PDF';

export type AssetKind = 'image' | 'video' | 'audio' | 'document' | 'text';

export function assetKind(file: { type?: string; name: string }): AssetKind | null {
  const ct = (file.type || '').toLowerCase();
  const n = file.name || '';
  if (ct === 'application/pdf' || /\.pdf$/i.test(n)) return 'document';
  if (ct === 'text/plain' || ct === 'text/markdown' || RX_TXT.test(n)) return 'text';
  if ((ct.startsWith('image/') && ct !== 'image/svg+xml') || RX_IMG.test(n)) return 'image';
  if (ct.startsWith('video/') || RX_VID.test(n)) return 'video';
  if (ct.startsWith('audio/') || RX_AUD.test(n)) return 'audio';
  return null;
}

/** null = OK. Otherwise the reason + (for too_big) the limit in MB.
 *  `allowText` — only the agent composer takes txt/md (planner context); asset
 *  surfaces (Assets page, file picker) keep rejecting them.
 *  `allowAudio` — only the home composer takes audio (LongVid uses it; HeyGen generate drops it). */
export function assetFileError(
  file: File,
  opts?: { allowText?: boolean; allowAudio?: boolean },
): { reason: 'unsupported' | 'too_big'; maxMb: number } | null {
  const kind = assetKind(file);
  // HeyGen's Video Agent rejects audio, so it stays unsupported except where allowAudio opts in.
  if (!kind || (kind === 'audio' && !opts?.allowAudio)) return { reason: 'unsupported', maxMb: 0 };
  if (kind === 'text' && !opts?.allowText) return { reason: 'unsupported', maxMb: 0 };
  const maxMb = kind === 'video' || kind === 'audio' ? 200 : kind === 'text' ? 2 : 32;
  if (file.size > maxMb * 1024 * 1024) return { reason: 'too_big', maxMb };
  return null;
}
